import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { Order, ShippingOption } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import Modal from '../../components/Modal';
import './AdminOrders.css';

interface OrdersResponse {
  orders: Order[];
  totalCount: number;
}

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);
  const [shippingOrderId, setShippingOrderId] = useState<number | null>(null);
  const [shipping, setShipping] = useState<boolean>(false);
  const [shippingOptions, setShippingOptions] = useState<ShippingOption[]>([]);
  const [selectedShippingOptionId, setSelectedShippingOptionId] = useState<number | null>(null);
  const [trackingNumber, setTrackingNumber] = useState<string>('');
  const [showShippingModal, setShowShippingModal] = useState<boolean>(false);
  const { success, error: showError, warning } = useToast();

  const fetchOrders = useCallback(async (): Promise<void> => {
    try {
      const params: Record<string, any> = { page, pageSize: 20 };
      if (statusFilter) params.status = statusFilter;
      const response = await api.get<OrdersResponse>('/admin/orders', { params });
      setOrders(response.data.orders);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const fetchShippingOptions = async (): Promise<void> => {
      try {
        const response = await api.get<ShippingOption[]>('/admin/shipping/options');
        setShippingOptions(response.data.filter(opt => (opt as any).isActive !== false));
      } catch (error) {
        console.error('Failed to fetch shipping options:', error);
      }
    };
    fetchShippingOptions();
  }, []);

  const updateOrderStatus = async (orderId: number, newStatus: string): Promise<void> => {
    setUpdatingOrderId(orderId);
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      fetchOrders();
    } catch (error) {
      showError('Failed to update order status.');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const handleShipClick = (orderId: number): void => {
    setShippingOrderId(orderId);
    setSelectedShippingOptionId(null);
    setTrackingNumber('');
    setShowShippingModal(true);
  };

  const shipOrder = async (): Promise<void> => {
    if (!shippingOrderId) return;
    if (!selectedShippingOptionId) {
      warning('Please select a shipping option.');
      return;
    }
    if (!trackingNumber.trim()) {
      warning('Tracking number is required.');
      return;
    }
    setShipping(true);
    try {
      await api.post(`/admin/orders/${shippingOrderId}/ship`, {
        shippingOptionId: selectedShippingOptionId,
        trackingNumber: trackingNumber.trim()
      });
      success('Order shipped successfully.');
      fetchOrders();
      setShowShippingModal(false);
      setShippingOrderId(null);
      setSelectedShippingOptionId(null);
      setTrackingNumber('');
    } catch (error) {
      showError('Failed to ship order.');
    } finally {
      setShipping(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading orders...</div>;
  }

  return (
    <div className="admin-orders">
      <div className="container">
        <div className="page-header">
          <Link to="/admin" className="back-button">
            <i className="fas fa-arrow-left"></i> Back to Admin Panel
          </Link>
          <h1 className="page-title">Order Management</h1>
        </div>

        <div className="filters">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="filter-select"
          >
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Processing">Processing</option>
            <option value="Shipped">Shipped</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>

        <div className="orders-table-container">
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Amount</th>
                <th>Gift</th>
                <th>Status</th>
                <th>Payment</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td>#{order.id}</td>
                  <td>
                    <div>
                      <div>{order.userName || order.guestEmail || order.userEmail || 'N/A'}</div>
                      <div className="email">{order.userEmail || order.guestEmail || 'N/A'}</div>
                    </div>
                  </td>
                  <td>{order.orderItems.length} item(s)</td>
                  <td>${order.totalAmount.toFixed(2)}</td>
                  <td>
                    {order.isGift ? (
                      <span title={order.giftMessage || 'Gift order'}>Gift</span>
                    ) : '—'}
                  </td>
                  <td>
                    <select
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                      className="status-select"
                      disabled={updatingOrderId === order.id}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Processing">Processing</option>
                      <option value="Shipped">Shipped</option>
                      <option value="Delivered">Delivered</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td>
                    <span className={`payment-status ${order.paymentStatus?.toLowerCase()}`}>
                      {order.paymentStatus || 'Pending'}
                    </span>
                  </td>
                  <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                  <td>
                    {order.status === 'Processing' && (
                      <button
                        onClick={() => handleShipClick(order.id)}
                        className="btn btn-primary btn-small"
                      >
                        Ship
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="btn btn-outline"
          >
            Previous
          </button>
          <span>Page {page} of {Math.ceil(totalCount / 20)}</span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page >= Math.ceil(totalCount / 20)}
            className="btn btn-outline"
          >
            Next
          </button>
        </div>
      </div>
      <Modal
        isOpen={showShippingModal}
        onClose={() => { setShowShippingModal(false); setShippingOrderId(null); }}
        title={`Ship Order #${shippingOrderId}`}
      >
            <form onSubmit={(e) => { e.preventDefault(); void shipOrder(); }}>
              <div className="form-group">
                <label htmlFor="shippingOption">Shipping Method *</label>
                <select
                  id="shippingOption"
                  value={selectedShippingOptionId || ''}
                  onChange={(e) => setSelectedShippingOptionId(parseInt(e.target.value) || null)}
                  required
                >
                  <option value="">Select shipping method...</option>
                  {shippingOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name} ({option.carrier}) - ${option.basePrice.toFixed(2)} + ${option.pricePerItem.toFixed(2)} per item
                    </option>
                  ))}
                </select>
                <small>Select the shipping method used for this order</small>
              </div>
              <div className="form-group">
                <label htmlFor="trackingNumber">Tracking Number *</label>
                <input
                  id="trackingNumber"
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Enter tracking number"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowShippingModal(false); setShippingOrderId(null); }} disabled={shipping}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={shipping}>
                  {shipping ? 'Shipping...' : 'Ship Order'}
                </button>
              </div>
            </form>
      </Modal>
    </div>
  );
};

export default AdminOrders;
